import React, { useEffect, useRef, useState } from "react";
import { Button, Box, Typography, Select, MenuItem } from "@mui/material";
import "./clientFormStyle.css";
import { ReactComponent as Flag } from "../../../assets/icons/client_icon/flag.svg";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useGlobalContext } from "../../../context/GlobalContext";
import CustomToast from "../CustomToast";
import { getIcon } from "../../../utils/iconConfig";
import useGetClientList from "./Hook/useGetClientList";
import useGetClientDetail from "./Hook/useGetClientDetail";
import { toast } from "react-hot-toast";
import { ReactComponent as WhiteDownArrow } from "../../../assets/icons/whiteDropdown.svg";
import apiClient from "../../../lib/api_client";
import { capitalEachLetter } from "../../../utils/capitalEachLetter";
import { lookupSavedCart } from "../../../features/cart/api/cartStore";

const ClientForm = () => {
  const [clientData, setClientData] = useState({
    id: "",
    name: "",
    newClientName: "",
    purchaseNo: "",
    emails: [],
    phoneNumbers: [],
    eventTypeId: "",
    eventName: "",
    isValid: false,
  });

  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const [error, setError] = useState({
    name: "",
    newClientName: "",
    purchaseNo: "",
    phoneNumbers: [],
    emails: [],
    event: "",
  });

  const [selectedClientCode, setSelectedClientCode] = useState(null);
  const [isSearchLocked, setIsSearchLocked] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const suggestionRefs = useRef([]);

  // const eventOptions = ["IIJS Premiere 2025"];

  const [eventOptions, setEventOptions] = useState([]);

  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const res = await apiClient.post("/getAllEventType", {
          pageParam: 1,
          limit: 1000,
        });

        const eventList = res?.data?.eventTypeData?.map((item) => ({
          id: item._id,
          name: item.eventTypeName,
        }));
        setEventOptions(eventList || []);
      } catch (err) {
        console.error("Failed to fetch event types:", err);
      }
    };

    fetchEventTypes();
  }, []);

  let clientNotFound = "Binnys Jewellery Pvt. Ltd. (Client not Found)";

  const { setClientForm } = useGlobalContext();

  const handlePurchaseOrderChange = (e) => {
    const value = e.target.value;
    setClientData((prev) => ({
      ...prev,
      purchaseNo: value,
    }));

    setError((prev) => ({
      ...prev,
      purchaseNo: "",
    }));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Unlock the search because user typed manually
    setIsSearchLocked(false);
    if (value.trim() === "" && clientData.name !== clientNotFound) {
      handleClearInput();
      return;
    }

    if (clientData.name === clientNotFound) {
      setClientData((prev) => ({
        ...prev,
        newClientName: value,
      }));
      
      setError((prev) => ({
        ...prev,
        newClientName: "",
      }));
      setIsSearchLocked(true);

      return;
    } else {
      setClientData((prev) => ({
        ...prev,
        name: value,
      }));
    }
  };

  const { data: clientList, isLoading } = useGetClientList(
    clientData.name,
    !isSearchLocked && !!clientData.name
  );

  useEffect(() => {
    if (isLoading) return;

    if (clientList && clientList.length) {
      setFilteredSuggestions(
        clientList.map((c) => ({
          client_name: c.clientName,
          client_code: c.clientCode,
        }))
      );
    } else if (clientData.name && !isSearchLocked) {
      setFilteredSuggestions([
        {
          client_name: clientNotFound,
          purchaseNo: "#00000",
          client_code: "",
        },
      ]);
    } else {
      setFilteredSuggestions([]);
    }
  }, [clientList, isLoading]);

  const handleEmailChange = (e, index) => {
    const value = e.target.value.trim();
    const updatedEmails = [...clientData.emails];

    const isGati = updatedEmails[index]?.isGati || false;

    updatedEmails[index] = {
      value,
      isGati,
    };

    setClientData((prev) => ({
      ...prev,
      emails: updatedEmails,
    }));

    setError((prev) => {
      let newErrors = [...prev.emails];

      if (value === "") {
        newErrors[index] = "";
      } else if (
        updatedEmails.filter((email) => email.value === value).length > 1
      ) {
        newErrors[index] = "Duplicate email is not allowed.";
      } else {
        newErrors[index] = "";
      }

      return { ...prev, emails: newErrors };
    });
  };

  const handlePhoneChange = (value, index) => {
    const updatedNumbers = [...clientData.phoneNumbers];
    const isGati = updatedNumbers[index]?.isGati || false;

    // const cleanNumber = value.replace(/\D/g, "").replace(/^91/, "");

    updatedNumbers[index] = {
      value: value,
      isGati: isGati,
    };

    setClientData((prev) => ({
      ...prev,
      phoneNumbers: updatedNumbers,
    }));

    setError((prev) => {
      const newErrors = [...prev.phoneNumbers];

      if (!value.trim()) {
        newErrors[index] = "Phone number is required.";
      } else if (
        value.trim() &&
        updatedNumbers
          .filter((num) => num.value && num.value !== "91")
          .filter((num) => num.value === value.trim()).length > 1
      ) {
        newErrors[index] = "Duplicate phone number is not allowed.";
      } else {
        newErrors[index] = "";
      }

      return { ...prev, phoneNumbers: newErrors };
    });
  };

  const handleSelectSuggestion = (suggestion) => {
    setIsSearchLocked(true);
    const inNewClient = suggestion.client_name === clientNotFound;

    setClientData((prev) => ({
      ...prev,
      id: inNewClient ? "" : suggestion.id, // Add this line
      name: suggestion.client_name,
      clientCode: inNewClient ? "BJPL" : suggestion.client_code,
      partyNo: inNewClient ? "1000001" : "",
      purchaseNo: "",
      newClientName: "",
      isValid: false,
    }));

    setSelectedClientCode(suggestion.client_code);
    setFilteredSuggestions([]);
    setError({
      name: "",
      purchaseNo: "",
      phoneNumbers: [],
      emails: [],
      eventTypeId: "",
      eventName: "",
    });
  };

  // Hook that fetches detail
  const { data: clientDetails } = useGetClientDetail(
    selectedClientCode,
    !!selectedClientCode
  );

  useEffect(() => {
    if (clientDetails) {
      setClientData((prev) => ({
        ...prev,
        id: clientDetails.id,
        emails: clientDetails.emails.length > 0 ? clientDetails.emails : [],
        phoneNumbers:
          clientDetails.mobileNumber.length > 0
            ? clientDetails.mobileNumber
            : [],
        partyNo: clientDetails.partyNo,
        isValid: true,
      }));
    } else {
      setClientData((prev) => ({
        ...prev,
        emails: [],
        phoneNumbers: [],
      }));
    }
  }, [clientDetails]);

  const handleAddandDeletePhone = (type, index) => {
    if (type === "ADD") {
      setClientData((prev) => ({
        ...prev,
        phoneNumbers: [...prev.phoneNumbers, { value: "", isGati: false }],
      }));
    }
    if (type === "DELETE") {
      setClientData((prev) => {
        const updatedNumbers = prev.phoneNumbers.filter((_, i) => i !== index);
        return { ...prev, phoneNumbers: updatedNumbers };
      });

      setError((prev) => {
        const newErrors = prev.phoneNumbers.filter((_, i) => i !== index);
        return { ...prev, phoneNumbers: newErrors };
      });
    }
  };

  const handleAddandDeleteEmaiil = (type, index) => {
    if (type === "ADD") {
      setClientData((prev) => ({
        ...prev,
        emails: [...prev.emails, { value: "", isGati: false }],
      }));
    }
    if (type === "DELETE") {
      setClientData((prev) => {
        const updatedEmails = prev.emails.filter((_, i) => i !== index);
        return { ...prev, emails: updatedEmails };
      });

      setError((prev) => {
        const newErrors = prev.emails.filter((_, i) => i !== index);
        return { ...prev, emails: newErrors };
      });
    }
  };

  // Function all clear input
  const handleClearInput = () => {
    setClientData({
      name: "",
      purchaseNo: "",
      emails: [],
      phoneNumbers: [],
      eventTypeId: "",
      eventName: "",
    });
    setError({
      name: "",
      newClientName: "",
      purchaseNo: "",
      phoneNumbers: [],
      emails: [],
      eventTypeId: "",
      eventName: "",
    });

    setSelectedClientCode("");

    setFilteredSuggestions([]);
  };

  const createBJPLDynamicClient = async() =>{
    try {
      const response = await apiClient.post("/createDynamicClient")

      return response?.data?.data
    } catch (error) {
      console.error("error",error?.message)
    }
  }

  const handleFormSubmit = () => {
    let isValid = true;
    let errors = {
      name: "",
      newClientName: "",
      purchaseNo: "",
      phoneNumbers: [],
      emails: [],
      eventTypeId: "",
      eventName: "",
    };

    const { name, eventTypeId } = clientData;

    // Client Name Validation
    if (name?.trim() === "") {
      errors.name = "Please enter a client name";
      isValid = false;
    } else {
      isValid = true;
    }

    if (eventTypeId?.trim() === "") {
      errors.eventTypeId = "Please select an event";
      isValid = false;
    } else {
      isValid = true;
    }

    // PO.NO. compulsory only for Binnys Jewellery Private Limited (BJPL)
    const isBJPL =
      clientData?.clientCode === "BJPL" ||
      clientData?.name === "Binnys Jewellery Private Limited";
    if (isBJPL && (!clientData?.purchaseNo || clientData?.purchaseNo?.trim() === "")) {
      errors.purchaseNo = "Please enter PO number";
      isValid = false;
    }

    const isValidPhone = (num) => {
      const trimmed = num?.trim();
      // const without91 = trimmed?.startsWith("91") ? trimmed?.slice(2) : trimmed;
      const without91 = trimmed;

      // return /^\d{10}$/.test(without91);
      return /^\d{12}$/.test(without91);
    };

    clientData?.phoneNumbers?.forEach((phone, index) => {
      if (!phone?.isGati) {
        const trimmed = phone?.value?.trim();
        // const without91 = trimmed?.startsWith("91") ? trimmed?.slice(2) : trimmed;
        const without91 = trimmed;

        if (trimmed?.length > 1 && without91 && !isValidPhone(trimmed)) {
          errors.phoneNumbers[index] =
            "Phone number must be exactly 10 digits.";
          isValid = false;
        } else {
          errors.phoneNumbers[index] = "";
        }
      }
    });

    const isValidEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    clientData?.emails?.forEach((email, index) => {
      const trimmed = email?.value?.trim();
      if (trimmed?.length > 0 && !isValidEmail(trimmed)) {
        errors.emails[index] = "Please enter a valid email address.";
        isValid = false;
      } else {
        errors.emails[index] = "";
      }
    });

    setError(errors);

    if (
      (!clientData?.isValid && clientData?.name != clientNotFound) ||
      (clientData?.name === clientNotFound && !clientData?.newClientName)
    ) {
      return toast.custom((t) => (
        <CustomToast
          t={t}
          isError={true}
          message="Please Enter Valid Client Details!"
        />
      ));
    }

    if (isValid) {
      setTimeout(async() => {
        setClientForm(true);
        const cleanedClientData = {
          ...clientData,
          emails: Array.isArray(clientData?.emails)
            ? clientData?.emails?.filter((e) => e?.value?.trim() !== "")
            : [],
          phoneNumbers: clientData?.phoneNumbers
            .filter((p) => p.value.trim() !== "")
            .map((p) => ({
              ...p,
              value: p.value,
            })),
        };

        localStorage.setItem("ClientData", JSON.stringify(cleanedClientData));
        setClientData({
          name: "",
          newClientName: "",
          purchaseNo: "",
          emails: [],
          phoneNumbers: [],
          eventTypeId: "",
          eventName: "",
        });

        if (
          clientData.clientCode === "BJPL" ||
          clientData.name === "Binnys Jewellery Private Limited"
        ) {
          const dynamicClient = await createBJPLDynamicClient();
          const storeDynamic = {
            dynamicClientName:dynamicClient?.dynamicClientName,
            id:dynamicClient?._id
          }
          localStorage.setItem("DynamicClient",JSON.stringify(storeDynamic))
        }
        
      }, 1500);
      toast.custom((t) => <CustomToast t={t} message="Form submitted... !" />);
    }
  };

  const handlePress = (e) => {
    if (e.key === "Enter") {
      const isInvalidClient =
        (!clientData?.isValid && clientData?.name !== clientNotFound) ||
        (clientData?.name === clientNotFound && !clientData?.newClientName);

      if (!isInvalidClient) {
        handleFormSubmit();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!filteredSuggestions || filteredSuggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      setActiveIndex((prevIndex) => {
        const nextIndex =
          prevIndex < filteredSuggestions.length - 1 ? prevIndex + 1 : 0;
        scrollToSuggestion(nextIndex);
        return nextIndex;
      });
    } else if (e.key === "ArrowUp") {
      setActiveIndex((prevIndex) => {
        const nextIndex =
          prevIndex > 0 ? prevIndex - 1 : filteredSuggestions.length - 1;
        scrollToSuggestion(nextIndex);
        return nextIndex;
      });
    } else if (e.key === "Enter" && activeIndex >= 0) {
      handleSelectSuggestion(filteredSuggestions[activeIndex]);
    }
  };

  const scrollToSuggestion = (index) => {
    suggestionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  const handleEventChange = (e) => {
    const eventTypeId = e.target.value;
    // The name is kept alongside the id because the saved cart is filed under
    // the client and the event, and a stored id on its own says nothing about
    // which event a cart belongs to.
    const eventName =
      eventOptions?.find((option) => option?.id === eventTypeId)?.name || "";

    setClientData((prev) => ({
      ...prev,
      eventTypeId,
      eventName,
    }));

    setError((prev) => ({
      ...prev,
      eventTypeId: "",
    }));
  };

  // Client and PO the lookup has already answered for, so filling the PO in
  // does not send the same question straight back.
  const lookedUpRef = useRef("");

  // A cart is filed under the client, the event and the PO together, so a cart
  // that exists already knows both. Picking a client fills in the PO number and
  // the event it was last carted under; editing the PO number then follows that
  // number to its own event. Either way the form lands on the key of a cart
  // that exists, which is what reopens it instead of starting an empty one.
  // A client or a PO with nothing saved leaves the fields exactly as they are.
  useEffect(() => {
    const clientCode = clientData?.clientCode?.trim();
    const purchaseNo = clientData?.purchaseNo?.trim();

    if (!clientCode) return;

    let cancelled = false;

    // Typed a character at a time, so the lookup waits for the number to stop
    // changing.
    const asked = `${clientCode}::${purchaseNo}`;
    if (lookedUpRef.current === asked) return;

    const timer = setTimeout(async () => {
      try {
        lookedUpRef.current = asked;
        const match = await lookupSavedCart({ clientCode, purchaseNo });
        if (cancelled || !match?.found) return;

        setClientData((prev) => {
          // The client or the PO moved on while the request was in flight, so
          // this answer is about something that is no longer in the form.
          if (
            prev?.clientCode?.trim() !== clientCode ||
            prev?.purchaseNo?.trim() !== purchaseNo
          ) {
            return prev;
          }

          const next = { ...prev };
          let changed = false;

          // Only filled when the box is empty - a PO the user typed is never
          // replaced by an older one.
          if (!purchaseNo && match.purchaseNo) {
            next.purchaseNo = match.purchaseNo;
            // The filled in number is the one this answer came from, so the
            // effect it re-triggers has nothing left to ask.
            lookedUpRef.current = `${clientCode}::${match.purchaseNo}`;
            changed = true;
          }

          if (match.eventTypeId && prev?.eventTypeId !== match.eventTypeId) {
            next.eventTypeId = match.eventTypeId;
            next.eventName = match.eventName || "";
            changed = true;
          }

          return changed ? next : prev;
        });

        setError((prev) => ({ ...prev, eventTypeId: "", purchaseNo: "" }));
      } catch (error) {
        // Nothing saved for this client or PO, or the lookup failed. The form
        // stays as it is and is filled in by hand as before.
        console.error("Saved cart lookup failed:", error);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clientData?.clientCode, clientData?.purchaseNo]);
  return (
    <>
      <Box width={"24.5vw"} margin="5vw auto" position="relative">
        <Box className="form-modal">
          <Typography className="form-header">Client Details</Typography>
          <Box onKeyDown={handlePress} className="modal-limit">
            <Box marginTop={"1.5vw"}>
              <Typography
                className="input-box-label"
                sx={{
                  visibility:
                    clientData?.name ===
                    "Binnys Jewellery Pvt. Ltd. (Client not Found)"
                      ? "hidden"
                      : "",
                }}
              >
                client name
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                className={`search-container ${
                  error?.name ? "error-search-container" : ""
                }`}
              >
                <span
                  style={{
                    height: "1vw",
                    width: "1vw",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {getIcon("Search", "dark")}
                </span>
                <input
                  type="text"
                  className="search-form-input"
                  placeholder="Search by Name or Code"
                  onKeyDown={handleKeyDown}
                  value={clientData?.name}
                  onChange={handleInputChange}
                  autoComplete="off"
                />
                <span className="clear-icon" onClick={handleClearInput}>
                  {clientData?.name && getIcon("Cross")}
                </span>
              </Box>
              {error?.name && (
                <Typography marginY=".35vw" className="error-message">
                  {error?.name}
                </Typography>
              )}
              {filteredSuggestions && filteredSuggestions.length > 0 && (
                <Box className="suggestions-dropdown">
                  {filteredSuggestions?.map((suggestion, index) => (
                    <div
                      key={index}
                      ref={(el) => (suggestionRefs.current[index] = el)}
                      className={`suggestion-item ${
                        activeIndex === index ? "active-suggestion" : ""
                      }`}
                      onClick={() => handleSelectSuggestion(suggestion)}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      {suggestion.client_name ===
                      "Binnys Jewellery Pvt. Ltd. (Client not Found)"
                        ? suggestion.client_name
                        : `${suggestion?.client_name} - #${suggestion?.client_code}`}
                    </div>
                  ))}
                </Box>
              )}
            </Box>
            <Box marginTop={"1.5vw"}>
              <Typography className="input-box-label">
                {clientData?.name ===
                "Binnys Jewellery Pvt. Ltd. (Client not Found)"
                  ? "client name"
                  : "PO.NO."}
              </Typography>
              {clientData?.name ===
              "Binnys Jewellery Pvt. Ltd. (Client not Found)" ? (
                <>
                  <input
                    type="text"
                    // name="newClientName"
                    className={
                      error?.newClientName ? "error-input-box" : "input-box"
                    }
                    placeholder="Enter Client Name"
                    value={clientData?.newClientName}
                    onChange={handleInputChange}
                  />
                  {error?.newClientName && (
                    <Typography marginY=".35vw" className="error-message">
                      {error?.newClientName}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="text"
                    className={
                      error?.purchaseNo ? "error-input-box" : "input-box"
                    }
                    placeholder="Purchase Order number"
                    value={clientData?.purchaseNo}
                    onChange={handlePurchaseOrderChange} //here
                  />
                  {error?.purchaseNo && (
                    <Typography marginY=".35vw" className="error-message">
                      {error?.purchaseNo}
                    </Typography>
                  )}
                </>
              )}
            </Box>
            <Box marginTop="1.5vw">
              <Typography className="input-box-label">Mobile No.</Typography>
              <Box>
                {(clientData?.phoneNumbers?.length > 0
                  ? clientData?.phoneNumbers
                  : [{ value: "", isGati: false }]
                ).map((phone, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    gap=".5vw"
                    flexWrap="wrap"
                    marginBottom=".25vw"
                  >
                    <Box flex="1">
                      <div style={{ position: "relative", width: "100%" }}>
                        <Flag
                          style={{
                            position: "absolute",
                            top: ".7vw  ",
                            left: ".75vw",
                            zIndex: "2",
                          }}
                        />
                        <PhoneInput
                          country={"in"}
                          disableDropdown
                          countryCodeEditable={false}
                          value={phone?.value ? `${phone.value}` : "91"}
                          onChange={(value) => handlePhoneChange(value, index)}
                          disabled={phone?.isGati}
                          inputStyle={{
                            width: "100%",
                            backgroundColor: "transparent",
                            color: "white",
                            height: "2.3vw",
                            borderRadius: ".4vw",
                            border: !error?.phoneNumbers[index]
                              ? "0.05vw solid #595959"
                              : "0.05vw solid #F23D3D",
                            fontSize: ".8vw",
                            fontFamily: "Poppins",
                            fontWeight: "500",
                            lineHeight: "100%",
                          }}
                          buttonStyle={{
                            display: "none",
                          }}
                          dropdownStyle={{
                            width: "17vw",
                            borderRadius: ".6vw",
                            backgroundColor: "#333",
                            color: "white",
                            padding: ".5vw",
                          }}
                        />
                        {(!phone?.value || phone?.value === "") && (
                          <span
                            style={{
                              position: "absolute",
                              top: "48%",
                              left: "4.5vw",
                              transform: "translateY(-50%)",
                              padding: ".25vw",
                              background: "transparent",
                              color: "#595959",
                              fontSize: ".8vw",
                              fontFamily: "Poppins",
                              fontWeight: "500",
                              pointerEvents: "none",
                            }}
                          >
                            Enter Mobile No.
                          </span>
                        )}
                      </div>

                      {error.phoneNumbers[index] && (
                        <Typography marginTop=".35vw" className="error-message">
                          {error.phoneNumbers[index]}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      display={
                        index === 0 && phone.value.length < 5 ? "none" : "block"
                      }
                      marginBottom={
                        error?.phoneNumbers[index] && "0.85vw !important"
                      }
                    >
                      {index === 0 ? (
                        <button
                          className="add-btn"
                          onClick={() => handleAddandDeletePhone("ADD")}
                        >
                          +
                        </button>
                      ) : (
                        <button
                          className="delete-btn"
                          style={{
                            pointerEvents: clientData?.phoneNumbers?.[index]
                              ?.isGati
                              ? "none"
                              : "auto",
                          }}
                          onClick={() =>
                            handleAddandDeletePhone("DELETE", index)
                          }
                        >
                          {getIcon("dustbin")}
                        </button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box marginTop="1.5vw">
              <Typography className="input-box-label">Email</Typography>
              <Box>
                {(clientData?.emails?.length > 0
                  ? clientData?.emails
                  : [{ value: "", isGati: false }]
                ).map((email, index) => {
                  return (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap=".5vw"
                      flexWrap="wrap"
                      marginBottom=".3vw"
                    >
                      <Box flex="1">
                        <input
                          className={
                            !error?.emails[index]
                              ? "input-box"
                              : "error-input-box"
                          }
                          type="email"
                          placeholder="Enter Email Address"
                          onChange={(e) => handleEmailChange(e, index)}
                          value={email?.value}
                          disabled={email?.isGati}
                        />
                        {error?.emails[index] && (
                          <Typography className="error-message" marginY="7px">
                            {error?.emails[index]}
                          </Typography>
                        )}
                      </Box>

                      {/* Add or Remove Button */}
                      <Box
                        display={
                          index === 0 && !email?.value?.includes(".com") > 0
                            ? "none"
                            : "block"
                        }
                        marginBottom={
                          error?.emails[index] && "1.5vw !important"
                        }
                      >
                        {index === 0 ? (
                          <button
                            className="add-btn"
                            onClick={() => handleAddandDeleteEmaiil("ADD")}
                          >
                            +
                          </button>
                        ) : (
                          <button
                            className="delete-btn"
                            onClick={() =>
                              handleAddandDeleteEmaiil("DELETE", index)
                            }
                            style={{
                              pointerEvents: clientData?.emails[index]?.isGati
                                ? "none"
                                : "auto",
                            }}
                          >
                            {/* <Remove /> */}
                            {getIcon("dustbin")}
                          </button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box marginTop="1.5vw">
              <Typography className="input-box-label">Event</Typography>
              <Box>
                <Select
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#EDEDEB" }}>Select Event</span>
                      );
                    }
                    const selectedEvent = eventOptions.find(
                      (e) => e.id === selected
                    );
                    return selectedEvent
                      ? capitalEachLetter(selectedEvent.name)
                      : "";
                  }}
                  // Bound to the id the form actually stores. It used to read a
                  // field nothing ever wrote, which left the Select holding its
                  // own state - a hand picked event showed up, but one filled in
                  // from a repeated PO number never did.
                  value={clientData?.eventTypeId || ""}
                  onChange={handleEventChange}
                  IconComponent={WhiteDownArrow}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        border: ".025vw solid #595959",
                        borderRadius: ".75vw",
                        backgroundColor: "#FFFFFF",
                        height: "fit-content",
                        width: "10vw",
                        fontSize: ".8vw",
                        fontFamily: "Poppins",
                        lineHeight: "100%",
                        color: "white",
                        paddingLeft: ".4vw",
                        outline: "none",
                        padding: ".25vw",
                        maxHeight: "9.3vw",
                        overflowY: "scroll",
                        "& .MuiList-root": {
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                        "& .MuiMenuItem-root": {
                          fontFamily: "Karla",
                          fontWeight: 500,
                          fontSize: ".9vw",
                          justifyContent: "start",
                          "&.Mui-selected": {
                            backgroundColor: "#262626",
                            color: "white",
                            borderRadius: ".25vw",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#262626",
                            color: "white",
                            borderRadius: ".25vw",
                          },
                          "&:hover": {
                            backgroundColor: "#262626",
                            color: "white",
                            borderRadius: ".25vw",
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    height: "2.3vw",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: ".8vw",
                    width: "100%",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ccc",
                      border: error?.eventTypeId
                        ? "0.05vw solid #f23d3d"
                        : ".025vw solid #595959",
                      borderRadius: ".25vw",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: error?.eventTypeId ? "#f23d3d" : "#595959",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #595959",
                      borderColor: "#595959",
                    },
                  }}
                >
                  {eventOptions?.map((data) => (
                    <MenuItem
                      sx={{
                        backgroundColor: "#FFFFFF",
                        color: "#262626",
                      }}
                      value={data.id}
                    >
                      {capitalEachLetter(data.name)}
                    </MenuItem>
                  ))}
                </Select>
                {error?.eventTypeId && (
                  <Typography marginY=".35vw" className="error-message">
                    {error?.eventTypeId}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          className="contBtn"
          onClick={() => handleFormSubmit()}
        >
          continue
        </Button>
      </Box>
    </>
  );
};

export default ClientForm;

